/* eslint-disable max-len */
import { Button, Modal } from 'antd';
import React, { useEffect } from 'react';
import { HocOptions } from './generic-hoc.types';
import GenericHoc from './generic-hoc';
import { useTour } from '@reactour/tour';
import { ReduxStore } from 'reducers/redux.types';
import { getSteps } from '../../constants';
import { useTranslation } from 'react-i18next';
import { ThemeType } from 'theme/jss-types';
import {
    MenuOutlined,
    CodeSandboxCircleFilled,
    PlusSquareFilled,
    SettingFilled,
    ThunderboltFilled,
    CloseOutlined,
} from '@ant-design/icons';

const styles = (theme : ThemeType) => ({
    title: {
        fontSize: '22px',
        fontWeight: 'bold',
        color: `${theme.primaryColor} !important`,
    },
    closeButton: {
        fontWeight: 700,
        border: 'none',
        color: '#8c8c8c',
        cursor: 'pointer',
        alignContent: 'center',
        marginRight: '8px',
        '&:hover': {
            color: '#262626',
        },
    },
    modal: {
        '& .ant-modal-content': {
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
        '& .ant-btn': {
            boxSizing: 'border-box',
            height: 30,
            borderRadius: 4,
            fontFamily: 'Open Sans',
            fontSize: 12,
            fontWeight: 600,
            laterSpacing: 0,
        },
        '& .ant-btn-primary, .ant-btn-primary:hover, .ant-btn-primary:focus': {
            backgroundColor: theme.primaryColor,
        },
        '& .ant-btn-default, .ant-btn-default:hover, .ant-btn-default:focus': {
            backgroundColor: '#ffffff',
            color: '#000000',
        },
    },
    hr: {
        width: '100%',
        height: 1,
        backgroundColor: '#eeeeee',
        margin: '16px 0px',
    },
    subTitle: {
        fontSize: '13px',
        color: '##000000',
    },
    steps: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        marginBottom: '20px',
    },
    stepName: {
        display: 'flex',
        alignItems: 'center',
    },
    progress: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
    },
    buttons: {
        display: 'flex',
        gap: '8px',
    },
    bannerPoints: {
        paddingTop: '10px',
    },
    bannerDescription: {
        alignSelf: 'center',
    },
    tourBannerPoints: {
        display: 'flex',
        paddingBottom: '10px',
    },
    bannerPointsIcon: {
        padding: '10px',
        alignSelf: 'center',
        marginRight: '10px',
        fontSize: 18,
        color: `${theme.primaryColor} !important`,
    },
    bannerPointsTitle: {
        fontWeight: 700,
        fontSize: 14,
    },
    actionButton: {
    },
});

const GetStartedModal = (props: any) => {
    const {
        isVisible, classes, cancelModal, routesInfo, master,
    } = props;

    const { t } = useTranslation();
    const { setIsOpen, setCurrentStep, setSteps } = useTour();

    const tourStepData = getSteps(master);
    const stepsTour = React.useMemo(() => {
        return tourStepData.filter((step) => {
            if (step.isVisible) {
                return true;
            }
            return false;
        });
    }, [routesInfo]);

    useEffect(() => {
        if (setSteps) {
            setSteps(stepsTour);
        }
    }, [stepsTour]);

    const onStepClick = () => {
        setCurrentStep(0);
        setIsOpen(true);
        cancelModal();
    };

    const renderTitle = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className={classes.title}>
                    🚀
                    {' '}
                    {t('welcome_title')}
                </div>
                <div className={classes.closeButton}><CloseOutlined onClick={cancelModal} /></div>
            </div>
        );
    };

    const renderBody = () => {
        return (
            <div className={classes.bannerPoints}>
                <div className={classes.tourBannerPoints}>
                    <div className={classes.bannerPointsIcon}>
                        <PlusSquareFilled />
                    </div>
                    <div className={classes.bannerDescription}>
                        <span className={classes.bannerPointsTitle}>
                            {t('modern_interface_title')}
                            {' '}
                        </span>
                        <span>{t('modern_interface_description')}</span>
                    </div>
                </div>
                <div className={classes.tourBannerPoints}>
                    <div className={classes.bannerPointsIcon}>
                        <MenuOutlined />
                    </div>
                    <div className={classes.bannerDescription}>
                        <span className={classes.bannerPointsTitle}>
                            {t('redesigned_navigation_title')}
                            {' '}
                        </span>
                        <span>{t('redesigned_navigation_description')}</span>
                    </div>
                </div>
                <div className={classes.tourBannerPoints}>
                    <div className={classes.bannerPointsIcon}>
                        <ThunderboltFilled />
                    </div>
                    <div className={classes.bannerDescription}>
                        <span className={classes.bannerPointsTitle}>
                            {t('introducing_quick_actions_title')}
                            {' '}
                        </span>
                        <span>{t('introducing_quick_actions_description')}</span>
                    </div>
                </div>
                <div className={classes.tourBannerPoints}>
                    <div className={classes.bannerPointsIcon}>
                        <CodeSandboxCircleFilled />
                    </div>
                    <div className={classes.bannerDescription}>
                        <span className={classes.bannerPointsTitle}>
                            {t('improved_shipment_management_title')}
                            {' '}
                        </span>
                        <span>{t('improved_shipment_management_description')}</span>
                    </div>
                </div>
                <div className={classes.tourBannerPoints}>
                    <div className={classes.bannerPointsIcon}>
                        <SettingFilled />
                    </div>
                    <div className={classes.bannerDescription}>
                        <span className={classes.bannerPointsTitle}>
                            {t('updated_ui_components_title')}
                            {' '}
                        </span>
                        <span>{t('updated_ui_components_description')}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            className={classes.modal}
            centered
            closable={false}
            visible={isVisible}
            width={650}
            footer={null}
            zIndex={1001}
        >
            <div>
                {renderTitle()}
                <div className={classes.hr} />
                <div>
                    {t('welcome_message')}
                </div>
                {renderBody()}
                <div>
                    <a href="https://shipsy-public-assets.s3.amazonaws.com/generic/file-786fb16e-8575-4632-90f7-cdceefb08721.pdf" target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', fontWeight: 600 }}>{t('click_here')}</a>
                    {' '}
                    {t('view_new_documentation')}
                </div>
                <div className={classes.hr} />
                <div style={{ display: 'flex', justifyContent: 'end', paddingTop: 4 }}>
                    <div className={classes.buttons}>
                        <Button
                            type="default"
                            className={classes.actionButton}
                            onClick={cancelModal}
                        >
                            {t('go_to_dashboard')}
                        </Button>
                        <Button
                            type="primary"
                            className={classes.actionButton}
                            onClick={() => onStepClick()}
                        >
                            {t('take_a_tour')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { miscellaneousState, master } = state;
    return {
        routesInfo: miscellaneousState?.ROUTES_INFO || {},
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(GetStartedModal);
